import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  UserIcon, UserGroupIcon,
  DocumentTextIcon, ClipboardDocumentListIcon, FolderIcon, InboxIcon, ArchiveBoxIcon,
  BanknotesIcon, CurrencyDollarIcon, CalculatorIcon, CreditCardIcon,
  ChartBarIcon, ChartPieIcon, PresentationChartBarIcon, TableCellsIcon,
  ComputerDesktopIcon, CpuChipIcon, WrenchScrewdriverIcon, CloudIcon, ServerIcon, CubeIcon,
  ChatBubbleLeftRightIcon, EnvelopeIcon, PhoneIcon, BellIcon, MegaphoneIcon,
  CalendarDaysIcon, ClockIcon,
  ShieldCheckIcon, LockClosedIcon, KeyIcon,
  HomeIcon, BuildingOfficeIcon, BriefcaseIcon, GlobeAltIcon,
  AcademicCapIcon, BookOpenIcon, StarIcon, MagnifyingGlassIcon,
  TruckIcon, ShoppingCartIcon, PrinterIcon, QrCodeIcon,
  RocketLaunchIcon, SparklesIcon,
} from '@heroicons/react/24/outline';
import { API } from '../contexts/AuthContext';
import { confirmDialog, errorAlert, successAlert } from '../helpers/alerts';

// Mapa nome -> componente. Os nomes seguem exatamente o padrao que o
// hub-centrao consome (valor final gravado como "hi:IconName" em
// dbo.Plataformas.icone). Manter em sincronia com hub-centrao/src/lib/icons.js.
const ICON_MAP = {
  UserIcon, UserGroupIcon,
  DocumentTextIcon, ClipboardDocumentListIcon, FolderIcon, InboxIcon, ArchiveBoxIcon,
  BanknotesIcon, CurrencyDollarIcon, CalculatorIcon, CreditCardIcon,
  ChartBarIcon, ChartPieIcon, PresentationChartBarIcon, TableCellsIcon,
  ComputerDesktopIcon, CpuChipIcon, WrenchScrewdriverIcon, CloudIcon, ServerIcon, CubeIcon,
  ChatBubbleLeftRightIcon, EnvelopeIcon, PhoneIcon, BellIcon, MegaphoneIcon,
  CalendarDaysIcon, ClockIcon,
  ShieldCheckIcon, LockClosedIcon, KeyIcon,
  HomeIcon, BuildingOfficeIcon, BriefcaseIcon, GlobeAltIcon,
  AcademicCapIcon, BookOpenIcon, StarIcon, MagnifyingGlassIcon,
  TruckIcon, ShoppingCartIcon, PrinterIcon, QrCodeIcon,
  RocketLaunchIcon, SparklesIcon,
};

const CATALOGO_ICONES = [
  { grupo: 'Pessoas', icones: ['UserIcon', 'UserGroupIcon'] },
  { grupo: 'Documentos', icones: [
    'DocumentTextIcon', 'ClipboardDocumentListIcon', 'FolderIcon',
    'InboxIcon', 'ArchiveBoxIcon',
  ] },
  { grupo: 'Financeiro', icones: [
    'BanknotesIcon', 'CurrencyDollarIcon', 'CalculatorIcon', 'CreditCardIcon',
  ] },
  { grupo: 'Graficos e dados', icones: [
    'ChartBarIcon', 'ChartPieIcon', 'PresentationChartBarIcon', 'TableCellsIcon',
  ] },
  { grupo: 'Tecnologia', icones: [
    'ComputerDesktopIcon', 'CpuChipIcon', 'WrenchScrewdriverIcon',
    'CloudIcon', 'ServerIcon', 'CubeIcon',
  ] },
  { grupo: 'Comunicacao', icones: [
    'ChatBubbleLeftRightIcon', 'EnvelopeIcon', 'PhoneIcon', 'BellIcon', 'MegaphoneIcon',
  ] },
  { grupo: 'Tempo', icones: ['CalendarDaysIcon', 'ClockIcon'] },
  { grupo: 'Seguranca', icones: ['ShieldCheckIcon', 'LockClosedIcon', 'KeyIcon'] },
  { grupo: 'Geral', icones: [
    'HomeIcon', 'BuildingOfficeIcon', 'BriefcaseIcon', 'GlobeAltIcon',
    'AcademicCapIcon', 'BookOpenIcon', 'StarIcon', 'MagnifyingGlassIcon',
    'TruckIcon', 'ShoppingCartIcon', 'PrinterIcon', 'QrCodeIcon',
    'RocketLaunchIcon', 'SparklesIcon',
  ] },
];

// Presets de cor usados historicamente no hub. rgb aqui e HEX com # (7 chars).
const CORES = [
  { label: 'Verde', value: '#16a34a' },
  { label: 'Laranja', value: '#ea580c' },
  { label: 'Azul', value: '#2563eb' },
  { label: 'Roxo', value: '#7c3aed' },
  { label: 'Rosa', value: '#db2777' },
  { label: 'Cinza', value: '#475569' },
];

const FORM_VAZIO = {
  nome: '', link: '', icone: '', rgb: '#16a34a',
};

function iconeLabel(name) {
  return String(name || '').replace(/Icon$/, '').replace(/([A-Z])/g, ' $1').trim();
}

// Extrai o nome do heroicon do valor gravado ("hi:XxxIcon" -> "XxxIcon").
// Aceita tambem apenas o nome cru como fallback.
function extrairNomeIcone(valor) {
  if (!valor) return null;
  const s = String(valor).trim();
  if (!s) return null;
  const nome = s.startsWith('hi:') ? s.slice(3) : s;
  return ICON_MAP[nome] ? nome : null;
}

const PICKER_CSS = `
.pf-picker { position: relative; }
.pf-picker__trigger {
  display: flex; align-items: center; gap: .5rem; width: 100%;
  padding: .35rem .6rem; border-radius: 8px; cursor: pointer;
  background: var(--portal-surface-soft); color: var(--portal-text);
  border: 1px solid var(--portal-border); font-size: .85rem; text-align: left;
  min-height: 34px;
}
.pf-picker__trigger:hover { border-color: #3ca8bc; }
.pf-picker__trigger--open { border-color: #3ca8bc; box-shadow: 0 0 0 3px rgba(60,168,188,.2); }
.pf-picker__preview { width: 20px; height: 20px; color: #3ca8bc; flex-shrink: 0; }
.pf-picker__label { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pf-picker__placeholder { color: var(--portal-muted); }
.pf-picker__chevron { width: 12px; height: 12px; opacity: .6; }

.pf-picker__panel {
  position: absolute; top: 100%; left: 0; right: 0; margin-top: 4px; z-index: 30;
  background: var(--portal-surface); border: 1px solid var(--portal-border);
  border-radius: 10px; box-shadow: 0 10px 30px rgba(0,0,0,.35);
  max-height: 340px; overflow-y: auto; padding: .5rem;
}
.pf-picker__search { padding: .3rem .5rem; font-size: .8rem; margin-bottom: .35rem; }
.pf-picker__group-label {
  font-size: .7rem; text-transform: uppercase; letter-spacing: .05em;
  color: var(--portal-muted); margin: .5rem .25rem .3rem; font-weight: 700;
}
.pf-picker__group:first-child .pf-picker__group-label { margin-top: .1rem; }
.pf-picker__grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: .25rem; }
.pf-picker__item {
  background: var(--portal-surface-soft); border: 1px solid transparent;
  border-radius: 6px; padding: .5rem; cursor: pointer; color: var(--portal-text);
  display: flex; align-items: center; justify-content: center;
  transition: background .1s, border-color .1s;
}
.pf-picker__item:hover { background: var(--portal-surface); border-color: #3ca8bc; }
.pf-picker__item--selected { background: #3ca8bc; color: #fff; border-color: #3ca8bc; }
.pf-picker__item svg { width: 22px; height: 22px; }
.pf-picker__empty { padding: .8rem; color: var(--portal-muted); text-align: center; font-size: .85rem; }
.pf-picker__actions {
  display: flex; gap: .3rem; padding-top: .4rem; border-top: 1px solid var(--portal-border);
  margin-top: .4rem;
}
.pf-picker__clear {
  flex: 1; padding: .35rem; font-size: .75rem; border-radius: 6px;
  background: var(--portal-surface-soft); border: 1px solid var(--portal-border);
  color: var(--portal-text); cursor: pointer;
}
.pf-picker__clear:hover { border-color: #3ca8bc; }
`;

function IconePicker({ valor, onChange }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const wrapRef = useRef(null);

  const selecionadoNome = extrairNomeIcone(valor);
  const SelecionadoIcon = selecionadoNome ? ICON_MAP[selecionadoNome] : null;

  useEffect(() => {
    if (!aberto) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [aberto]);

  const termo = busca.trim().toLowerCase();
  const gruposFiltrados = termo
    ? CATALOGO_ICONES
      .map((g) => ({ ...g, icones: g.icones.filter((n) => n.toLowerCase().includes(termo)) }))
      .filter((g) => g.icones.length > 0)
    : CATALOGO_ICONES;

  const selecionar = (nome) => {
    onChange(`hi:${nome}`);
    setAberto(false);
    setBusca('');
  };

  const limpar = () => {
    onChange('');
    setAberto(false);
    setBusca('');
  };

  return (
    <div className="pf-picker" ref={wrapRef}>
      <button
        type="button"
        className={`pf-picker__trigger ${aberto ? 'pf-picker__trigger--open' : ''}`}
        onClick={() => setAberto((v) => !v)}
      >
        {SelecionadoIcon ? (
          <>
            <SelecionadoIcon className="pf-picker__preview" />
            <span className="pf-picker__label">{iconeLabel(selecionadoNome)}</span>
          </>
        ) : (
          <span className="pf-picker__label pf-picker__placeholder">
            — Sem icone (usa inicial do nome) —
          </span>
        )}
        <i className="bi bi-chevron-down pf-picker__chevron" />
      </button>

      {aberto && (
        <div className="pf-picker__panel">
          <input
            className="form-control form-control-sm pf-picker__search"
            placeholder="Buscar icone (ex: cloud, chart, user...)"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            autoFocus
          />

          {gruposFiltrados.length === 0 ? (
            <div className="pf-picker__empty">Nenhum icone encontrado.</div>
          ) : (
            gruposFiltrados.map((g) => (
              <div className="pf-picker__group" key={g.grupo}>
                <div className="pf-picker__group-label">{g.grupo}</div>
                <div className="pf-picker__grid">
                  {g.icones.map((nome) => {
                    const Icon = ICON_MAP[nome];
                    if (!Icon) return null;
                    return (
                      <button
                        key={nome}
                        type="button"
                        className={`pf-picker__item ${selecionadoNome === nome ? 'pf-picker__item--selected' : ''}`}
                        title={iconeLabel(nome)}
                        onClick={() => selecionar(nome)}
                      >
                        <Icon />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}

          <div className="pf-picker__actions">
            <button type="button" className="pf-picker__clear" onClick={limpar}>
              <i className="bi bi-x-circle me-1" /> Remover icone
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Plataformas() {
  const [plataformas, setPlataformas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState(FORM_VAZIO);
  const [busca, setBusca] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/plataformas`);
      setPlataformas(res.data?.data || []);
      setErro('');
    } catch (e) {
      setErro('Nao foi possivel carregar as plataformas.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const setCampo = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  const salvar = async (e) => {
    e?.preventDefault?.();
    const nome = form.nome.trim();
    const link = form.link.trim();
    if (!nome || !link) {
      errorAlert({ title: 'Campos obrigatorios', text: 'Informe nome e link.' });
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        nome,
        link,
        icone: form.icone.trim() || null,
        rgb: form.rgb.trim() || null,
      };
      const res = await axios.post(`${API}/admin/plataformas`, payload);
      const nova = res.data?.data;
      if (nova) setPlataformas((ps) => [...ps, nova].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')));
      setForm(FORM_VAZIO);
      successAlert({ title: 'Plataforma cadastrada', text: `${nome} agora aparece no hub.` });
    } catch (err) {
      errorAlert({
        title: 'Falha ao cadastrar',
        text: err.response?.data?.message || 'Nao foi possivel cadastrar.',
      });
    } finally {
      setSalvando(false);
    }
  };

  const remover = async (p) => {
    const r = await confirmDialog({
      title: 'Excluir plataforma?',
      text: `"${p.nome}" sera removida do hub. Essa acao nao pode ser desfeita.`,
      confirmButtonText: 'Excluir',
    });
    if (!r.isConfirmed) return;
    try {
      await axios.delete(`${API}/admin/plataformas/${p.id}`);
      setPlataformas((ps) => ps.filter((x) => x.id !== p.id));
    } catch (err) {
      errorAlert({
        title: 'Falha ao excluir',
        text: err.response?.data?.message || 'Nao foi possivel excluir.',
      });
    }
  };

  const plataformasFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return plataformas;
    return plataformas.filter((p) => (
      [p.nome, p.link, p.icone].filter(Boolean).join(' ').toLowerCase().includes(termo)
    ));
  }, [plataformas, busca]);

  return (
    <div className="content-shell">
      <style>{PICKER_CSS}</style>
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">Administracao</span>
          <h1>Plataformas do Hub</h1>
          <p>
            Cadastro das plataformas exibidas no <strong>Hub Centrao</strong>
            (landing page compartilhada). Grava direto em <code>dbo.Plataformas</code>.
          </p>
        </div>
      </section>

      {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}

      <section className="content-card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>
          <i className="bi bi-plus-circle me-2" />Nova plataforma
        </h2>
        <form onSubmit={salvar} className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Nome *</label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Ex: Portal RH"
              value={form.nome}
              maxLength={255}
              onChange={(e) => setCampo('nome', e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Link *</label>
            <input
              type="url"
              className="form-control form-control-sm"
              placeholder="https://sistema.centraomateriais.com"
              value={form.link}
              maxLength={255}
              onChange={(e) => setCampo('link', e.target.value)}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Icone (heroicon)</label>
            <IconePicker valor={form.icone} onChange={(v) => setCampo('icone', v)} />
            <small className="text-muted">
              Gravado como <code>hi:NomeDoIcone</code>. O mesmo desenho renderiza no hub.
            </small>
          </div>

          <div className="col-md-6">
            <label className="form-label">Cor de destaque</label>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {CORES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCampo('rgb', c.value)}
                  title={c.label}
                  style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: c.value,
                    border: form.rgb === c.value ? '3px solid var(--portal-text)' : '2px solid var(--portal-border)',
                    cursor: 'pointer',
                  }}
                />
              ))}
              <input
                type="color"
                value={form.rgb}
                onChange={(e) => setCampo('rgb', e.target.value)}
                title="Cor personalizada"
                style={{
                  width: 40, height: 32, border: '1px solid var(--portal-border)',
                  borderRadius: 6, background: 'transparent', padding: 2,
                }}
              />
              <code style={{ marginLeft: '.5rem' }}>{form.rgb}</code>
            </div>
          </div>

          <div className="col-12 d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setForm(FORM_VAZIO)}
              disabled={salvando}
            >
              Limpar
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={salvando}>
              <i className="bi bi-save me-1" />
              {salvando ? 'Cadastrando…' : 'Cadastrar plataforma'}
            </button>
          </div>
        </form>
      </section>

      <div className="filter-card">
        <div className="filter-card__field filter-card__field--grow">
          <i className="bi bi-search" />
          <input
            type="text"
            placeholder="Buscar por nome, link ou icone..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
      </div>

      <section className="content-card">
        {loading ? (
          <div className="content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando plataformas...</p>
          </div>
        ) : plataformasFiltradas.length ? (
          <div className="table-shell">
            <table className="portal-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>ID</th>
                  <th>Nome</th>
                  <th>Link</th>
                  <th style={{ width: 200 }}>Icone</th>
                  <th style={{ width: 90 }}>Cor</th>
                  <th style={{ width: 90 }}>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {plataformasFiltradas.map((p) => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td><strong>{p.nome}</strong></td>
                    <td>
                      <a href={p.link} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>
                        {p.link}
                      </a>
                    </td>
                    <td>
                      {(() => {
                        const nome = extrairNomeIcone(p.icone);
                        const Icon = nome ? ICON_MAP[nome] : null;
                        if (!Icon) return <span className="text-muted">—</span>;
                        return (
                          <span className="d-inline-flex align-items-center gap-2">
                            <Icon style={{ width: 20, height: 20, color: '#3ca8bc' }} />
                            <code style={{ fontSize: '.7rem' }}>{p.icone}</code>
                          </span>
                        );
                      })()}
                    </td>
                    <td>
                      {p.rgb ? (
                        <span className="d-inline-flex align-items-center gap-2">
                          <span style={{
                            display: 'inline-block', width: 18, height: 18,
                            borderRadius: '50%', background: p.rgb,
                            border: '1px solid var(--portal-border)',
                          }}
                          />
                          <code style={{ fontSize: '.7rem' }}>{p.rgb}</code>
                        </span>
                      ) : <span className="text-muted">—</span>}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => remover(p)}
                        title="Excluir"
                      >
                        <i className="bi bi-trash" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="content-card--centered">
            <i className="bi bi-inbox" style={{ fontSize: '2rem', opacity: .4 }} />
            <p>Nenhuma plataforma cadastrada.</p>
          </div>
        )}
      </section>
    </div>
  );
}
